<script>
  import { setContext, getContext } from "svelte";
  import { ENTRIES } from "./app_config.js";
  import t from "#lib/i18n.svelte.js";
  import { fetch_json, entry_url } from "#lib/util.js";
  import { refreshAll, goto } from '$app/navigation';
  import MainPageContent from "#lib/MainPageContent.svelte";
  
  let { 
    entry_id, 
    onclose,
    onsave,
    oncancel,
    has_unsaved_changes = $bindable(false),
  } = $props();
  
  // This is "sort of" considered derived state initially (see effect)
  // let entry = $state(new ENTRIES[raw_entry.type].model(raw_entry));
  let entry = $state(undefined);
  let show_loading = $state(false);
  // The parent entry_api
  // let entry_api = getContext('entry_api');
  let first_block_background_color = $derived(entry?.body?.blocks?.[0]?.background_color);
  let is_editing = $state(true);
  let active_container = $state(undefined);
  
  // Canvas toolbar menu state (for coordinating visibility between block and embed toolbars)
  let active_canvas_menu = $state(null);
  
  // Processing state for save/cancel operations (prevents double-clicks)
  const LOADING_DELAY_MS = 200;
  let is_processing = $state(false);
  let show_processing = $state(false);
  let processing_timer = null;
  
  /**
   * Start processing state with delayed indicator
   */
  function start_processing() {
    is_processing = true;
    processing_timer = setTimeout(() => {
      if (is_processing) show_processing = true;
    }, LOADING_DELAY_MS);
  }
  
  /**
   * End processing state and clear indicator
   */
  function end_processing() {
    is_processing = false;
    show_processing = false;
    if (processing_timer) {
      clearTimeout(processing_timer);
      processing_timer = null;
    }
  }
  
  // Call this (from different) places to reset 
  // E.g. when <PlainText> calls this when not provided with an explicit focus property.
  function clear_selection() {
    active_container?.clear_selection();
    active_container = undefined;
  }

  function start_editing() {
    is_editing = true;
    clear_selection();
  }

  function cancel_editing() {
    if (is_processing) return; // Prevent double-click
    is_processing = true; // Block interactions without showing "saving" indicator
    oncancel?.();
    onclose?.();
  }

  async function save() {
    if (is_processing) return; // Prevent double-click
    start_processing();
    
    const new_or_updated_entry = entry.to_json();
    try {
      if (new_or_updated_entry.entry_id) {
        await fetch_json('POST', '/api/update-entry', new_or_updated_entry);
        onsave?.();
        onclose?.();
      }
    } catch (err) {
      console.error(err);
      end_processing();
      alert('There was an error. You can try again, but before that, please just copy and paste your article into a safe place.');
      return;
    }
    is_editing = false;
    clear_selection();
  }

  // Provide ENTRIES via context to avoid circular import issues
  setContext("entry_types", ENTRIES);

  setContext("entry_api", {
    is_contextual_editing: true,
    get is_disabled() {
      return false;
    },
    get is_processing() {
      return is_processing;
    },
    get show_processing() {
      return show_processing;
    },
    get page_title() {
      return entry.preview.title;
    },
    get page_description() {
      return entry.preview.description;
    },
    get entry() {
      return entry;
    },
    get active_container() {
      return active_container;
    },
    set active_container(_active_container) {
      // we clear the selection of the other container, when switching
      active_container?.clear_selection();
      active_container = _active_container;
    },
    get is_editing() {
      return is_editing;
    },
    set is_editing(_is_editing) {
      is_editing = _is_editing;
    },
    clear_selection,
    cancel_editing,
    start_editing,
    save,
    // Canvas toolbar menu coordination
    get active_canvas_menu() { return active_canvas_menu; },
    set_active_canvas_menu: (id) => { active_canvas_menu = id; },
  });
  
  $effect(() => {
    // Show loading indicator only after a delay to prevent flicker on fast loads
    const loading_timer = setTimeout(() => {
      if (!entry) show_loading = true;
    }, LOADING_DELAY_MS);
    
    // Load entry from server
    (async () => {
      const raw_entry = await fetch_json('POST', '/api/entry', { entry_id });
      entry = new ENTRIES[raw_entry.type].model(raw_entry);
    })();
    
    return () => {
      clearTimeout(loading_timer);
      entry?.dispose?.();
    };
  });
</script>
  <!-- {@html `<style>:root{--first-block-background-color: ${first_block_background_color}}</style>`} -->

<div class="in-place-editor">
  {#if entry}
    <MainPageContent
      id={entry.type + '-' + entry.entry_id}
      {is_editing}
    >
      {@const EntryTypeComponent = ENTRIES[entry.type].component}
      <EntryTypeComponent {entry} {is_editing} /> 
    </MainPageContent>
  {:else if show_loading}
    loading...
  {/if}
</div>